/* suite.io — Data Service Layer (trimmed)
   Reads from the same Firestore as nyc-suite.
   Only includes collections suite.io needs right now. */

import { db, doc, getDoc, setDoc, updateDoc, collection, getDocs, deleteDoc, query, onSnapshot, runTransaction } from "../firebase.js";
import { writeBatch } from "firebase/firestore";
import { checkRevision, mergeDoc } from "./revisionCheck.js";

/* Key-Value Store (app collection) */
const kv = {
  get: async (key) => {
    try {
      const snap = await getDoc(doc(db, "app", key));
      if (snap.exists()) return JSON.parse(snap.data().value);
      return null;
    } catch (e) {
      console.error("kv.get:", e);
      return null;
    }
  },
  /** The same read, throwing on failure: a caller that must tell "no document" from
   *  "the read failed" (the quote builder's recovery copy on load). */
  fetch: async (key) => {
    const snap = await getDoc(doc(db, "app", key));
    return snap.exists() ? JSON.parse(snap.data().value) : null;
  },
  /** Read a raw (non-JSON-wrapped) document from the app collection. */
  getRaw: async (key) => {
    try {
      const snap = await getDoc(doc(db, "app", key));
      return snap.exists() ? snap.data() : null;
    } catch (e) {
      console.error("kv.getRaw:", e);
      return null;
    }
  },
  set: async (key, val) => {
    try {
      await setDoc(doc(db, "app", key), { value: JSON.stringify(val), updatedAt: new Date().toISOString() });
    } catch (e) {
      console.error("kv.set:", e);
      throw e; // Re-throw so callers can handle/rollback
    }
  },
  delete: async (key) => {
    try {
      await deleteDoc(doc(db, "app", key));
    } catch (e) {
      console.error("kv.delete:", e);
      throw e;
    }
  },
};

/* Collection Factory */
// serverOwned: top-level fields only the server writes. save and bulkSave leave
// them out, so a whole-record merge from a tab's cached copy never puts back an
// older value (a job's delivery switches: setJobAutomation owns them, with its
// checks, typed confirmations and audit; docs/ingest/15 section 5.1).
function createCollection(name, { serverOwned = [] } = {}) {
  const writable = (record) => {
    const out = stripUndefined(record);
    for (const k of serverOwned) delete out[k];
    return out;
  };
  return {
    loadAll: async () => {
      try {
        const snap = await getDocs(collection(db, name));
        return snap.docs.map((d) => ({ ...d.data(), _docId: d.id }));
      } catch (e) {
        console.error(`loadAll(${name}):`, e);
        return [];
      }
    },
    get: async (id) => {
      try {
        const snap = await getDoc(doc(db, name, id));
        return snap.exists() ? { ...snap.data(), _docId: snap.id } : null;
      } catch (e) {
        console.error(`get(${name}):`, e);
        return null;
      }
    },
    // The same read, throwing on failure: a caller that must tell a missing document
    // (null) from a failed read (quote Discard edits reloads the stored record).
    fetch: async (id) => {
      const snap = await getDoc(doc(db, name, id));
      return snap.exists() ? { ...snap.data(), _docId: snap.id } : null;
    },
    save: async (record) => {
      const docId = record.id || `${name.slice(0, 2)}_${Date.now()}`;
      await setDoc(doc(db, name, docId), {
        ...writable(record),
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      return docId;
    },
    // The same merge save, refused when the document isn't the revision the caller
    // loaded: `expected` is { exists, savedAt } (a second tab or computer saved or
    // deleted it meanwhile), or undefined to skip the check. The error carries code
    // "changed-elsewhere" or "deleted-elsewhere". `keepFromStored` names fields the
    // stored document keeps as they are now (the lists' own fields), read inside the
    // transaction so a list action from another computer during the save survives.
    // Returns the document as committed (the stored one merged with the payload, kept
    // fields included), so the caller installs what is really stored, not what it sent.
    saveChecked: async (record, expected, { keepFromStored = [] } = {}) => {
      const docId = record.id || `${name.slice(0, 2)}_${Date.now()}`;
      const ref = doc(db, name, docId);
      let committed = null;
      await runTransaction(db, async (tx) => {
        const payload = { ...writable(record), updatedAt: new Date().toISOString() };
        const snap = await tx.get(ref);
        const stored = snap.exists() ? snap.data() : null;
        if (expected !== undefined) checkRevision(snap.exists(), stored?.savedAt, expected);
        if (stored) for (const f of keepFromStored) delete payload[f];
        tx.set(ref, payload, { merge: true });
        committed = mergeDoc(stored, payload);
      });
      return { id: docId, record: { ...committed, _docId: docId } };
    },
    // Field patch: only the given keys travel (plus updatedAt), applied to a
    // document that must already exist. updateDoc rejects on a missing one,
    // so a patch can never leave a partial record behind for a card that was
    // deleted meanwhile (a merge write would have created one); callers send
    // the whole record first (DataContext.saveCard does).
    patch: async (id, patch) => {
      await updateDoc(doc(db, name, id), {
        ...writable(patch),
        updatedAt: new Date().toISOString(),
      });
      return id;
    },
    delete: async (id) => {
      await deleteDoc(doc(db, name, id));
    },
    bulkSave: async (records) => {
      const BATCH = 50;
      let saved = 0;
      for (let i = 0; i < records.length; i += BATCH) {
        const batch = records.slice(i, i + BATCH);
        await Promise.all(batch.map((r) => {
          const docId = r.id || `${name.slice(0, 1)}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
          return setDoc(doc(db, name, docId), {
            ...writable(r),
            updatedAt: new Date().toISOString(),
          }, { merge: true });
        }));
        saved += batch.length;
      }
      return saved;
    },
    bulkDelete: async (ids) => {
      const BATCH = 50;
      let deleted = 0;
      for (let i = 0; i < ids.length; i += BATCH) {
        const batch = ids.slice(i, i + BATCH);
        await Promise.all(batch.map((id) => deleteDoc(doc(db, name, id))));
        deleted += batch.length;
      }
      return deleted;
    },
    // `opts.metadata`: the listener also fires on metadata changes
    // (includeMetadataChanges), so the acknowledgement of this client's own
    // write is delivered, and every document carries `_pending`: whether this
    // client has a write to it the server has not acknowledged yet. Only
    // crewCards asks for it; every other collection gets the snapshot exactly
    // as before (no options, no `_pending`).
    subscribe: (callback, opts = {}) => {
      try {
        const q = query(collection(db, name));
        const onNext = (snap) => {
          const docs = snap.docs.map((d) => (opts.metadata
            ? { ...d.data(), _docId: d.id, _pending: d.metadata.hasPendingWrites }
            : { ...d.data(), _docId: d.id }));
          callback(docs, snap.metadata);
        };
        const onError = (err) => { console.error(`subscribe(${name}):`, err); };
        return opts.metadata ? onSnapshot(q, { includeMetadataChanges: true }, onNext, onError) : onSnapshot(q, onNext, onError);
      } catch (e) {
        console.error(`subscribe(${name}):`, e);
        return () => {};
      }
    },
  };
}

/* Files Store
   Canonical key: job.id. Same shape as nyc-suite's files collection so
   Jobs data written by either app lands in the same Firestore docs. */
const files = {
  load: async (jobId) => {
    try {
      const snap = await getDoc(doc(db, "files", jobId));
      return snap.exists() ? (snap.data().files || []) : [];
    } catch (e) {
      console.error("files.load:", e);
      return [];
    }
  },
  // Read-only view of canonical + legacy-keyed files — same merge as
  // loadAndMigrate WITHOUT the migration write. For surfaces that must never
  // write (the crew Files tab); admins migrate via loadAndMigrate.
  loadMerged: async (canonicalId, legacyId) => {
    try {
      const queries = [getDoc(doc(db, "files", canonicalId))];
      if (legacyId && legacyId !== canonicalId) {
        queries.push(getDoc(doc(db, "files", legacyId)));
      }
      const [canonSnap, legacySnap] = await Promise.all(queries);
      const canonFiles = canonSnap?.exists() ? (canonSnap.data().files || []) : [];
      const legacyFiles = legacySnap?.exists() ? (legacySnap.data().files || []) : [];
      if (legacyFiles.length === 0) return canonFiles;
      const seen = new Set(canonFiles.map((f) => f.fileName));
      return [...canonFiles, ...legacyFiles.filter((f) => !seen.has(f.fileName))];
    } catch (e) {
      console.error("files.loadMerged:", e);
      return [];
    }
  },
  loadAndMigrate: async (canonicalId, legacyId) => {
    try {
      const queries = [getDoc(doc(db, "files", canonicalId))];
      if (legacyId && legacyId !== canonicalId) {
        queries.push(getDoc(doc(db, "files", legacyId)));
      }
      const [canonSnap, legacySnap] = await Promise.all(queries);
      const canonFiles = canonSnap?.exists() ? (canonSnap.data().files || []) : [];
      const legacyFiles = legacySnap?.exists() ? (legacySnap.data().files || []) : [];
      if (legacyFiles.length === 0) return canonFiles;
      const seen = new Set(canonFiles.map((f) => f.fileName));
      const merged = [...canonFiles, ...legacyFiles.filter((f) => !seen.has(f.fileName))];
      const batch = writeBatch(db);
      batch.set(doc(db, "files", canonicalId), {
        jobId: canonicalId,
        files: merged,
        updatedAt: new Date().toISOString(),
      });
      batch.delete(doc(db, "files", legacyId));
      await batch.commit();
      return merged;
    } catch (e) {
      console.error("files.loadAndMigrate:", e);
      return [];
    }
  },
  save: async (jobId, filesArr) => {
    await setDoc(doc(db, "files", jobId), {
      jobId,
      files: filesArr,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
  },
};

function stripUndefined(obj) {
  const { _docId, ...data } = obj;
  return JSON.parse(JSON.stringify(data));
}

/* Users Collection — proper Firestore docs (not JSON string) */
const users = {
  get: async (uid) => {
    try {
      const snap = await getDoc(doc(db, "users", uid));
      return snap.exists() ? { ...snap.data(), _docId: snap.id } : null;
    } catch (e) {
      console.error("users.get:", e);
      return null;
    }
  },
  getByEmail: async (email) => {
    try {
      const snap = await getDocs(collection(db, "users"));
      const match = snap.docs.find((d) => d.data().email?.toLowerCase() === email.toLowerCase());
      return match ? { ...match.data(), _docId: match.id } : null;
    } catch (e) {
      console.error("users.getByEmail:", e);
      return null;
    }
  },
  save: async (uid, data) => {
    await setDoc(doc(db, "users", uid), {
      ...data,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
  },
  subscribe: (callback) => {
    try {
      const q = query(collection(db, "users"));
      return onSnapshot(q, (snap) => {
        const docs = snap.docs.map((d) => ({ ...d.data(), _docId: d.id }));
        callback(docs);
      }, (err) => {
        console.error("users.subscribe:", err);
      });
    } catch (e) {
      console.error("users.subscribe:", e);
      return () => {};
    }
  },
};

/* Jobs-era collections (Jobs app rebuild — J0 plumbing).
   Same Firestore collections nyc-suite reads/writes. Each is the
   standard createCollection() shape (loadAll/save/delete/subscribe/bulk). */
const quotes = createCollection("quotes");
// A job's delivery switches (requireReview, autoSend) change only through the
// server (setJobAutomation): a Jobs-app save never writes `delivery`.
// `registration` (the /r/ page: consent statement, state, public code) is
// written field by field by the Rigs Settings tab and by the server (the
// studio walk-ins code moves from month to month: functions/src/services/
// studioWalkins.ts), never by the job card; a whole-record save from a cached
// copy must not put back an older statement or a code that moved. Neither
// does the walk-in marker `studioWalkins`, which only the server writes.
const jobs = createCollection("jobs", { serverOwned: ["delivery", "registration", "studioWalkins"] });
const contacts = createCollection("contacts");
const clients = createCollection("clients");
const docs = createCollection("docs");
const contentBlocks = createCollection("contentBlocks");
const docTemplates = createCollection("docTemplates");
const campaigns = createCollection("campaigns");
/* CRM v3 slice 4: saved audiences. Admin-only, like every other CRM
   collection — the governance session ships the rule. */
const segments = createCollection("segments");
const aiConversations = createCollection("aiConversations");
const tasks = createCollection("tasks");
const entities = createCollection("entities"); // read-only legacy

/* Website-era assessments collection (shared with jobs app — same docs). */
const assessmentsCollection = createCollection("assessments");

/* Holds (docs/holds/holds-plan.md): holds/{quoteId}, the crew-readable
   projection of an open quote's dated sessions. Written ONLY by the
   holdsProjection Cloud Function triggers; the rules deny every client write,
   so this store exposes reads only (the whole collection: list + subscribe). */
const holdsReadOnly = createCollection("holds");
const holds = {
  loadAll: holdsReadOnly.loadAll,
  subscribe: holdsReadOnly.subscribe,
};

/* Feedback: suite-io's website app uses `feedback` as nested subcollection
   helpers under plannerSessions. nyc-suite's DataContext exports a flat
   `feedback` collection but doesn't call it in J0, so we keep suite-io's
   existing nested API under the same `feedback` export — no breakage. */
const feedback = {
  save: async (sessionId, data, parentCollection = "plannerSessions") => {
    const feedbackId = `fb_${crypto.randomUUID()}`;
    await setDoc(doc(db, parentCollection, sessionId, "feedback", feedbackId), {
      ...stripUndefined(data),
      createdAt: new Date().toISOString(),
    });
    return feedbackId;
  },
  loadForSession: async (sessionId, parentCollection = "plannerSessions") => {
    try {
      const snap = await getDocs(collection(db, parentCollection, sessionId, "feedback"));
      return snap.docs.map((d) => ({ ...d.data(), _docId: d.id, _sessionId: sessionId }));
    } catch (e) {
      console.error("feedback.loadForSession:", e);
      return [];
    }
  },
  update: async (sessionId, feedbackId, updates, parentCollection = "plannerSessions") => {
    await setDoc(doc(db, parentCollection, sessionId, "feedback", feedbackId), {
      ...updates,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
  },
};

export const dataService = {
  kv,
  users,
  files,
  // Crew Cards (docs/crew-cards/DESIGN.md): card-only fields per job session.
  // Deploy snapshots live in crewCardDeploys, server-only, never read here.
  crewCards: createCollection("crewCards"),
  clientCards: createCollection("clientCards"),
  plannerSessions: createCollection("plannerSessions"),
  quoteSubmissions: createCollection("quoteSubmissions"),

  // Jobs-era collections
  quotes,
  jobs,
  contacts,
  clients,
  docs,
  contentBlocks,
  docTemplates,
  campaigns,
  segments,
  aiConversations,
  tasks,
  entities,
  assessments: assessmentsCollection,
  holds,

  // Website app feedback (nested under plannerSessions). DataContext does not
  // touch feedback.* in J0, so keeping the existing nested API under the
  // `feedback` export is safe for the Jobs layer.
  feedback,

  /* Load the data DataContext expects on mount. Mirrors nyc-suite's loadAll().
     Returns docFolders + studioSettings + draft the same way so DataContext's
     initial-load block can be ported verbatim.

     Role-aware (holds slice 2): `admin: false` skips the admin-only business
     data (quotes, contacts, clients, docs, assessments, campaigns, segments,
     aiConversations, tasks, the builder draft, doc folders) so a crew or viewer
     browser never requests collections the rules would refuse. Jobs, holds, and
     the studio settings load for every role. */
  loadAll: async ({ admin = true } = {}) => {
    const empty = async () => [];
    const none = async () => null;
    // The recovery copy: "none" and "the read failed" are told apart, since a failed
    // read must not let the builder's autosave write over a copy nobody has seen.
    const draftRead = async () => {
      try { return { draft: await kv.fetch("nycap-draft"), failed: false }; }
      catch (e) { console.error("kv.fetch(nycap-draft):", e); return { draft: null, failed: true }; }
    };
    const [
      quotesData, jobsData, contactsData, clientsData, docsData,
      assessmentsData, campaignsData, segmentsData, aiConversationsData, tasksData,
      draftResult, ppEq, ppCr, ppSt, ppTr, docFolders, ppRoles, holdsData,
    ] = await Promise.all([
      admin ? quotes.loadAll() : empty(),
      jobs.loadAll(),
      admin ? contacts.loadAll() : empty(),
      admin ? clients.loadAll() : empty(),
      admin ? docs.loadAll() : empty(),
      admin ? assessmentsCollection.loadAll() : empty(),
      admin ? campaigns.loadAll() : empty(),
      admin ? segments.loadAll() : empty(),
      admin ? aiConversations.loadAll() : empty(),
      admin ? tasks.loadAll() : empty(),
      admin ? draftRead() : Promise.resolve({ draft: null, failed: false }),
      kv.get("pp-equipment"),
      kv.get("pp-crew"),
      kv.get("pp-storage"),
      kv.get("pp-transport"),
      admin ? kv.get("doc-folders") : none(),
      kv.get("pp-roles"),
      holds.loadAll(),
    ]);
    return {
      quotes: quotesData,
      jobs: jobsData,
      contacts: contactsData,
      clients: clientsData,
      docs: docsData,
      assessments: assessmentsData,
      campaigns: campaignsData,
      segments: segmentsData,
      aiConversations: aiConversationsData,
      tasks: tasksData,
      holds: holdsData,
      draft: draftResult.draft,
      draftReadFailed: draftResult.failed,
      docFolders: docFolders || [],
      studioSettings: { equipment: ppEq, crewRoster: ppCr, storageOpts: ppSt, transportOpts: ppTr, roleOpts: ppRoles },
    };
  },
};
